/**
 * EF-CLIENT-PORTAL-UI-V1
 * Client Portal - Main entry point and dashboard
 * EF-CLIENT-ROLE-FOUNDATION-V1: Added role-based access control
 * 
 * Features:
 * - Active projects summary
 * - Upcoming shifts
 * - Recent documents
 * - Navigation to sub-pages
 * - Role-based UI (admin vs viewer)
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, Outlet, useLocation } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { ScrollArea } from "../../components/ui/scroll-area";
import { toast } from "sonner";
import {
  Briefcase,
  Calendar,
  Users,
  FileText,
  Receipt,
  Loader2,
  LogOut,
  Menu,
  X,
  ChevronRight,
  Clock,
  Building,
  Sun,
  Moon,
  Home,
  Inbox,
  Shield,
} from "lucide-react";
import { useTheme } from "../../components/theme/ThemeProvider";
// EVENTSFLO-GUIDE-BUTTON-ROLLOUT-V1
import { GuideButton } from "../../components/common/GuideButton";
import ClientPortalContext, { createClientPortalContextValue, CLIENT_ROLE } from "../../contexts/ClientPortalContext";
// EVENTSFLO-PORTAL-I18N-LANGUAGE-SELECTOR-PHASE1
import { useI18n } from "../../i18n/I18nProvider";
import { LanguageSelector } from "../../i18n/LanguageSelector";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Token storage key for client portal
const CLIENT_TOKEN_KEY = "eventsflo_client_token";

/**
 * Client Portal Layout with navigation
 */
export default function ClientPortalPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, setTheme } = useTheme();
  // EVENTSFLO-PORTAL-I18N-LANGUAGE-SELECTOR-PHASE1
  const { t } = useI18n();
  
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [error, setError] = useState(null);

  // Get auth token
  const getToken = useCallback(() => {
    return localStorage.getItem(CLIENT_TOKEN_KEY);
  }, []);

  // Fetch client profile
  const fetchProfile = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setError("Not authenticated");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/client/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem(CLIENT_TOKEN_KEY);
          setError("Session expired");
          return;
        }
        throw new Error("Failed to fetch profile");
      }

      const data = await response.json();
      setProfile(data);
    } catch (err) {
      console.error("Profile fetch error:", err);
      setError(err.message);
    }
  }, [getToken]);

  // Fetch dashboard data
  const fetchDashboard = useCallback(async () => {
    const token = getToken();
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/client/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to fetch dashboard");

      const data = await response.json();
      setDashboard(data);
    } catch (err) {
      console.error("Dashboard fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchProfile();
    fetchDashboard();
  }, [fetchProfile, fetchDashboard]);

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem(CLIENT_TOKEN_KEY);
    navigate("/login");
  };

  // Toggle theme
  const toggleTheme = () => {
    const newTheme = theme === "dark" ? "light" : "dark";
    setTheme(newTheme);
  };

  const isActive = (path) => {
    if (path === "/client") return location.pathname === "/client";
    return location.pathname.startsWith(path);
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="pt-6 text-center">
            <p className="text-destructive mb-4">{error}</p>
            <Button onClick={() => navigate("/login")}>Go to Login</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // EF-CLIENT-ROLE-FOUNDATION-V1: Create context value with role info
  const contextValue = createClientPortalContextValue(profile);

  // Navigation items - EF-CLIENT-ROLE-FOUNDATION-V1: Team page only for admins
  // EVENTSFLO-CLIENT-PORTAL-PHASE-1.5-QUOTES-INVOICES-V1: Added Quotes + Invoices
  // EVENTSFLO-CLIENT-PORTAL-PHASE-2-PROJECT-SHIFT-REQUESTS-V1: Added Requests (any role)
  const navItems = [
    { path: "/client", label: t("clientPortal.nav.dashboard"), icon: Home, testKey: "dashboard" },
    { path: "/client/project-requests", label: t("clientPortal.nav.requests"), icon: Inbox, testKey: "requests" },
    { path: "/client/projects", label: t("clientPortal.nav.projects"), icon: Briefcase, testKey: "projects" },
    { path: "/client/shifts", label: t("clientPortal.nav.shifts"), icon: Calendar, testKey: "shifts" },
    { path: "/client/crew", label: t("clientPortal.nav.crew"), icon: Users, testKey: "crew" },
    { path: "/client/quotes", label: t("clientPortal.nav.quotes"), icon: FileText, testKey: "quotes" },
    { path: "/client/invoices", label: t("clientPortal.nav.invoices"), icon: Receipt, testKey: "invoices" },
    { path: "/client/documents", label: t("clientPortal.nav.documents"), icon: FileText, testKey: "documents" },
    ...(contextValue.isAdmin ? [{ path: "/client/team", label: t("clientPortal.nav.team"), icon: Shield, testKey: "team" }] : []),
  ];

  // Dashboard view (when at /client exactly)
  const isDashboard = location.pathname === "/client";

  return (
    <ClientPortalContext.Provider value={contextValue}>
    <div className="min-h-screen bg-background">
      {/* Top Navigation Bar */}
      <header className="sticky top-0 z-50 bg-card border-b border-border">
        <div className="flex items-center justify-between h-14 px-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMenuOpen(!menuOpen)}
              className="md:hidden"
              data-testid="mobile-menu-toggle"
            >
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            {/* EF-PWA-BRANDING-V1: Dynamic company logo */}
            {/* EVENTSFLO-CLIENT-PROFILE-CUSTOMISATION-LOGO-V1: prefer
                client-account logo + portal_display_name when set;
                fall back to company logo + client_name. */}
            <div className="flex items-center gap-2">
              {dashboard?.client_logo_url ? (
                <img
                  src={dashboard.client_logo_url}
                  alt={dashboard.portal_display_name || dashboard.client_name || "Client"}
                  className="h-7 w-auto max-w-[120px] object-contain"
                  data-testid="client-portal-logo"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              ) : dashboard?.company_logo_url ? (
                <img
                  src={dashboard.company_logo_url}
                  alt={dashboard.company_name || "Company"}
                  className="h-7 w-auto max-w-[120px] object-contain"
                  data-testid="company-logo"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling?.classList?.remove('hidden');
                  }}
                />
              ) : null}
              {!dashboard?.client_logo_url && !dashboard?.company_logo_url && (
                <Building className="h-5 w-5 text-primary" />
              )}
              <span
                className="font-semibold text-sm truncate max-w-[150px] sm:max-w-none"
                data-testid="client-portal-display-name"
              >
                {profile?.portal_display_name ||
                  dashboard?.portal_display_name ||
                  profile?.client_name ||
                  "Client Portal"}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* EF-CLIENT-ROLE-FOUNDATION-V1: Show role badge */}
            {contextValue.isAdmin && (
              <Badge variant="outline" className="hidden sm:flex items-center gap-1 bg-primary/10 text-primary border-primary/20">
                <Shield className="h-3 w-3" />
                {t("clientPortal.admin")}
              </Badge>
            )}
            {/* EVENTSFLO-PORTAL-I18N-LANGUAGE-SELECTOR-PHASE1: Client Portal supports EN/FR */}
            <LanguageSelector
              variant="compact"
              exclude={["ru"]}
              testId="client-language-selector"
            />
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              data-testid="theme-toggle"
            >
              {theme === "dark" ? (
                <Sun className="h-4 w-4" />
              ) : (
                <Moon className="h-4 w-4" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-1 px-4 pb-2">
          {navItems.map((item) => (
            <Button
              key={item.path}
              variant={isActive(item.path) ? "secondary" : "ghost"}
              size="sm"
              onClick={() => navigate(item.path)}
              className="gap-2"
              data-testid={`nav-${item.testKey || item.label.toLowerCase()}`}
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </Button>
          ))}
        </nav>
      </header>

      {/* Mobile Menu Overlay */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setMenuOpen(false)}
        />
      )}

      {/* Mobile Slide-out Menu */}
      <aside
        className={`fixed top-14 left-0 z-40 h-[calc(100vh-3.5rem)] w-64 bg-card border-r border-border transform transition-transform duration-200 md:hidden ${
          menuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <ScrollArea className="h-full py-4">
          <nav className="flex flex-col gap-1 px-2">
            {navItems.map((item) => (
              <Button
                key={item.path}
                variant={isActive(item.path) ? "secondary" : "ghost"}
                className="w-full justify-start gap-3"
                onClick={() => {
                  navigate(item.path);
                  setMenuOpen(false);
                }}
                data-testid={`mobile-nav-${item.testKey || item.label.toLowerCase()}`}
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </Button>
            ))}
          </nav>
        </ScrollArea>
      </aside>

      {/* Main Content */}
      <main className="p-4 md:p-6 max-w-7xl mx-auto">
        {isDashboard ? (
          <ClientDashboard dashboard={dashboard} navigate={navigate} t={t} />
        ) : (
          <Outlet context={{ profile, getToken }} />
        )}
      </main>
    </div>
    </ClientPortalContext.Provider>
  );
}

/**
 * EF-CLIENT-PORTAL-UI-REFINEMENT-V1
 * Dashboard component - refined layout
 * Order: Upcoming Shifts → Active Projects → Recent Documents
 */
function ClientDashboard({ dashboard, navigate, t }) {
  if (!dashboard) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const hasUpcomingShifts = dashboard.upcoming_shift_list?.length > 0;
  const hasProjects = dashboard.recent_project_list?.length > 0;

  // Format date for display
  const formatShiftDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.toDateString() === today.toDateString()) return "Today";
    if (date.toDateString() === tomorrow.toDateString()) return "Tomorrow";
    return date.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold" data-testid="dashboard-title">{t("clientPortal.welcome")}</h1>
          <p className="text-muted-foreground text-sm">
            {hasUpcomingShifts 
              ? `You have ${dashboard.upcoming_shifts || 0} upcoming shift${dashboard.upcoming_shifts !== 1 ? 's' : ''}`
              : "No upcoming shifts scheduled"
            }
          </p>
        </div>
        {/* EVENTSFLO-GUIDE-BUTTON-ROLLOUT-V1 */}
        <GuideButton testId="client-dashboard-guide-btn" />
      </div>

      {/* SECTION 1: Upcoming Shifts (Priority) */}
      {hasUpcomingShifts ? (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Calendar className="h-4 w-4 text-primary" />
                Upcoming Shifts
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/client/shifts")}
                className="gap-1 text-xs h-8"
              >
                View All <ChevronRight className="h-3 w-3" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="divide-y divide-border">
              {dashboard.upcoming_shift_list.slice(0, 5).map((shift) => (
                <div
                  key={shift.id}
                  className="flex items-center gap-4 py-3 first:pt-0 last:pb-0"
                  data-testid={`upcoming-shift-${shift.id}`}
                >
                  {/* Date Badge */}
                  <div className="w-16 shrink-0 text-center">
                    <div className="bg-primary/10 text-primary rounded-lg py-2 px-1">
                      <p className="text-xs font-medium">{formatShiftDate(shift.date)}</p>
                      <p className="text-[10px] text-primary/70">{shift.start_time}</p>
                    </div>
                  </div>
                  
                  {/* Shift Details */}
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm truncate">{shift.name}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {shift.project_name}
                    </p>
                    {shift.venue_name && (
                      <p className="text-xs text-muted-foreground truncate flex items-center gap-1 mt-0.5">
                        <span className="inline-block w-1 h-1 bg-muted-foreground rounded-full"></span>
                        {shift.venue_name}
                      </p>
                    )}
                  </div>
                  
                  {/* Time */}
                  <div className="text-right shrink-0">
                    <p className="text-xs text-muted-foreground">
                      {shift.start_time} - {shift.end_time}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-dashed">
          <CardContent className="py-8 text-center">
            <Calendar className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
            <p className="text-sm text-muted-foreground">{t("clientPortal:dashboard.noUpcomingShifts")}</p>
            <Button
              variant="link"
              size="sm"
              onClick={() => navigate("/client/shifts")}
              className="mt-2"
            >
              View all shifts
            </Button>
          </CardContent>
        </Card>
      )}

      {/* SECTION 2: Active Projects */}
      {hasProjects && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-primary" />
                Your Projects
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/client/projects")}
                className="gap-1 text-xs h-8"
              >
                View All <ChevronRight className="h-3 w-3" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid gap-2">
              {dashboard.recent_project_list.slice(0, 4).map((project) => (
                <div
                  key={project.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-accent/30 hover:bg-accent/50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/client/projects/${project.id}`)}
                  data-testid={`recent-project-${project.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm truncate">{project.name}</p>
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                        {project.status}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {project.total_shifts || 0} shift{project.total_shifts !== 1 ? 's' : ''}
                      {project.upcoming_shifts > 0 && (
                        <span className="text-primary ml-2">
                          • {project.upcoming_shifts} upcoming
                        </span>
                      )}
                    </p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* SECTION 3: Quick Links (compact) */}
      <div className="grid grid-cols-3 gap-3">
        <Card
          className="cursor-pointer hover:bg-accent/50 transition-colors"
          onClick={() => navigate("/client/projects")}
        >
          <CardContent className="py-4 text-center">
            <Briefcase className="h-5 w-5 mx-auto text-muted-foreground mb-1" />
            <p className="text-lg font-semibold">{dashboard.active_projects || 0}</p>
            <p className="text-[10px] text-muted-foreground">{t("clientPortal:dashboard.projects")}</p>
          </CardContent>
        </Card>
        <Card
          className="cursor-pointer hover:bg-accent/50 transition-colors"
          onClick={() => navigate("/client/crew")}
        >
          <CardContent className="py-4 text-center">
            <Users className="h-5 w-5 mx-auto text-muted-foreground mb-1" />
            <p className="text-lg font-semibold">{dashboard.total_crew || 0}</p>
            <p className="text-[10px] text-muted-foreground">Crew</p>
          </CardContent>
        </Card>
        <Card
          className="cursor-pointer hover:bg-accent/50 transition-colors"
          onClick={() => navigate("/client/documents")}
        >
          <CardContent className="py-4 text-center">
            <FileText className="h-5 w-5 mx-auto text-muted-foreground mb-1" />
            <p className="text-lg font-semibold">{dashboard.recent_documents || 0}</p>
            <p className="text-[10px] text-muted-foreground">{t("clientPortal:dashboard.documents")}</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

